import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { IconLeft, IconDelete } from "../../../../../assets/icons";
import { Button, Form, Input, message, Switch } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { CheckOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { v4 as uuid } from "uuid";
import api from "../../../../../api/api";
import { localStorage } from "@utils";

const RootContainer = styled.div`
  position: relative;
  height: 100%;

  .ant-form {
    .buttons-wrapper {
      position: absolute;
      top: -70px;
      right: 12px;
    }

    .ant-btn {
      height: 48px;
      width: 172px;
    }
  }
`;

const Container = styled.div`
  margin-top: 24px;
  height: 100%;
  border-radius: 8px;
  padding: 24px;
  background: ${(props) => props.theme.backgroundColor};
`;

const Topbar = styled.div`
  padding-top: 16px;
  padding-bottom: 24px;
  display: flex;
  gap: 18px;
  align-items: center;
  justify-content: center;

  .ant-btn-default {
    height: 48px;
    padding: 0 12px !important;
    border: none !important;
    background: ${(props) => props.theme.backgroundColor} !important;
    color: ${(props) => props.theme.primaryColor} !important;
  }

  .ant-btn-primary {
    width: 172px;
    height: 48px;
  }
`;

const TitleWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 400;
  color: #1d1014;
  line-height: 1;
`;

const GrayTitle = styled(Title)`
  color: #868686;
`;

const TitleBar = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  ${Title} {
    flex: 1;
  }

  .ant-btn-primary {
    width: 172px;
    height: 48px;
  }
`;

const ItemCard = styled.div`
  .ant-form {
    width: 100%;
    border-radius: 8px;
    background: #fff;
    padding: 24px 16px;
    margin-bottom: 24px;
    display: flex;
    gap: 16px;
    align-items: center;
    justify-content: center;

    .ant-input {
      width: 100%;
    }

    .ant-form-item {
      margin-bottom: 0;

      :nth-child(2),
      :nth-child(3) {
        width: 100%;
      }

      :first-child {
        width: 90px;

        * {
          width: 90px;
        }
      }
    }
  }

  .icon-delete {
    width: 48px;
    min-width: 48px;
    height: 48px;
    cursor: pointer;
  }
`;

const Item = ({ index, item, handleUpdateClicked, handleDeleteClicked }) => {
  const [form] = Form.useForm();

  const storeLang = localStorage.getStoreLang() || [];
  const langCount = storeLang?.length;

  useEffect(() => {
    form.setFieldValue("is_active", item.is_active);
    form.setFieldValue("primary", item.primary);
    form.setFieldValue("secondary", item.secondary);
  }, [item]);

  const onFieldsChange = () => {
    handleUpdateClicked({
      id: item.id,
      ...form.getFieldsValue(),
    });
  };

  return (
    <ItemCard>
      <Form
        form={form}
        initialValues={{
          is_active: true,
        }}
        onFieldsChange={onFieldsChange}
      >
        <Form.Item>
          <Input value={index + 1} />
        </Form.Item>
        <Form.Item name={"primary"}>
          <Input placeholder={`City Name (${storeLang[0]?.language})`} />
        </Form.Item>
        {langCount === 2 &&
        <Form.Item name={"secondary"}>
          <Input placeholder={`City Name (${storeLang[1]?.language})`} />
        </Form.Item>
          }   
        <Form.Item name={"is_active"} valuePropName="checked">
          <Switch />
        </Form.Item>
        <IconDelete
          className="icon-delete"
          onClick={() => handleDeleteClicked(item.id)}
        />
      </Form>
    </ItemCard>
  );
};

const AddCity = () => {
  const { country_id, country_name } = useParams();
  const navigate = useNavigate();

    const storeLang = localStorage.getStoreLang() || [];
  const langCount = storeLang?.length;

  const [data, setData] = useState([
    {
      id: uuid(),
      primary: "",
      secondary: "",
      is_active: true,
    },
  ]);

  const [disabledSave, setDisabledSave] = useState(true);
  const [confirmLoading, setConfirmLoading] = useState(false);

  useEffect(() => {
    let _isEmpty = data.find((x) => isEmpty(x.primary) || (langCount === 2 && isEmpty(x.secondary)));
    setDisabledSave(_isEmpty || data?.length === 0);
  }, [data]);

  const isEmpty = (value) => !value || value.trim() === "";

  const handleSave = () => {
    if (!confirmLoading) {
      setConfirmLoading(true);
      const payload = {
        country_id,
        cities: data.map(({ is_active, primary, secondary }) => ({
          is_active: !!is_active,
          name: { primary:primary, secondary: langCount === 2 ? secondary : null },
        })),
      };
      api
        .createCities(payload)
        .then(() => {
          setConfirmLoading(false);
          message.success("Added the city successfully!");
          goBack();
        })
        .catch((error) => {
          setConfirmLoading(false);
          message.error(error?.message || "Something went wrong.");
        });
    }
  };

  const handleAddCityClicked = () => {
    setData((prev) => [
      ...prev,
      {
        id: uuid(),
        primary: "",
        secondary: "",
        is_active: true,
      },
    ]);
  };

  const handleUpdateClicked = (item) => {
    setData((prev) => prev.map((x) => (x.id === item.id ? item : x)));
  };

  const handleDeleteClicked = (id) => {
    setData((prev) => prev.filter((item) => item.id !== id));
  };

  const goBack = () => {
    navigate(-1);
  };

  return (
    <RootContainer>
      <Topbar>
        <Button type="default" icon={<IconLeft />} onClick={goBack}>
          Back
        </Button>
        <TitleWrapper>
          <GrayTitle>Delivery Area</GrayTitle>
          <GrayTitle>{`>`}</GrayTitle>
          <Title>Add City</Title>
        </TitleWrapper>
        <Button
          type="primary"
          disabled={disabledSave}
          icon={<CheckOutlined />}
          onClick={handleSave}
          loading={confirmLoading}
        >
          Save
        </Button>
      </Topbar>

      <TitleBar>
        <Title>{`Add City in ${country_name}`}</Title>
        <Button
          type="primary"
          icon={<PlusCircleOutlined />}
          onClick={handleAddCityClicked}
        >
          Add City
        </Button>
      </TitleBar>

      <Container>
        {data.map((item, key) => (
          <Item
            key={item.id}
            index={key}
            item={item}
            handleUpdateClicked={handleUpdateClicked}
            handleDeleteClicked={handleDeleteClicked}
          />
        ))}
      </Container>
    </RootContainer>
  );
};

export default AddCity;
